import React, { useEffect, useState, lazy, memo, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, CreditCard, Users, Star, XCircle, FileText, Brain, HelpCircle, TrendingDown, MessageCircle, Clock, CalendarX, GraduationCap, BookOpen, Briefcase, Target, Check, X, ChevronDown, MapPin, BadgeCheck } from 'lucide-react';
const Navbar = () => <nav className="sticky top-0 z-50 bg-[#F8F7FC]/85 backdrop-blur-md border-b border-card-border" data-id="element-0"></nav>;
const Hero = () => <header className="text-center relative overflow-hidden pt-[30px] pb-[30px]" data-id="element-1">
    <div className="absolute top-[-200px] left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-hero-glow pointer-events-none" data-id="element-2"></div>
    <div className="wrap relative z-10 pl-[20px] pr-[20px]" data-id="element-3">
      <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.08em] uppercase text-gold-light bg-gold/10 px-3.5 py-1.5 rounded-full border border-gold/25 mb-5" data-id="element-4">
        Sistema N.º 1 en Argentina para estudiantes de Contador Público
      </span>
      <h1 className="font-display font-bold text-[clamp(30px,5vw,52px)] leading-[1.08] mb-5 text-navy tracking-[-0.01em]" data-id="element-5">
        Dejá de atrasarte con tus materias:
        <br data-id="element-6" />
        aprobá tu próximo parcial en{' '}
        <span className="text-gold-light" data-id="element-7">14 días</span>
      </h1>
      <img src="https://cdn.magicpatterns.com/uploads/pmhJB6iS4jGbhYYAQs8Nv4/ChatGPT_Image_17_jun_2026,_12_02_35_p.m..png" alt="Kit Contador al Día — caja con los 6 tomos, bonos, planilla y método C.A.R.G.A sobre un escritorio" className="w-[88%] sm:w-[70%] max-w-[740px] mx-auto mb-7 rounded-2xl border border-card-border shadow-[0_20px_50px_rgba(0,0,0,0.45)]" data-id="element-8" />

      <div className="flex flex-wrap gap-2.5 justify-center mb-9" data-id="element-9">
        {['PDF Digital', 'Acceso inmediato', '1.º a 5.º año'].map(tag => <span key={tag} className="text-[13px] font-semibold px-4 py-1.5 rounded-full bg-card-bg border border-card-border text-text-secondary" data-id="element-10">
            {tag}
          </span>)}
      </div>
      <div className="flex flex-col items-center gap-3.5" data-id="element-11">
        <a href="#oferta" className="inline-flex items-center justify-center gap-2.5 font-bold px-8 py-4.5 rounded-xl bg-btn-primary text-[#1a1205] shadow-btn-primary hover:-translate-y-0.5 hover:shadow-btn-primary-hover active:translate-y-0 active:scale-98 transition-all text-center w-full sm:w-auto text-[14px]" data-id="element-12">
          💥 Quiero mi materia resuelta ahora
        </a>
      </div>
      <div className="flex flex-wrap gap-6 justify-center mt-12 pt-8 border-t border-card-border" data-id="element-13">
        <div className="flex items-center gap-2 text-[13px] text-text-tertiary font-semibold" data-id="element-14">
          <ShieldCheck className="w-[18px] h-[18px] text-gold-light shrink-0" data-id="element-15" />{' '}
          Garantía 7 días
        </div>
        <div className="flex items-center gap-2 text-[13px] text-text-tertiary font-semibold" data-id="element-16">
          <CreditCard className="w-[18px] h-[18px] text-gold-light shrink-0" data-id="element-17" />{' '}
          Pago seguro · Mercado Pago
        </div>
        <div className="flex items-center gap-2 text-[13px] text-text-tertiary font-semibold" data-id="element-18">
          <Users className="w-[18px] h-[18px] text-gold-light shrink-0" data-id="element-19" /> +500
          estudiantes
        </div>
        <div className="flex items-center gap-2 text-[13px] text-text-tertiary font-semibold" data-id="element-20">
          <Star className="w-[18px] h-[18px] text-gold-light shrink-0 fill-gold-light" data-id="element-21" />{' '}
          4.9 / 5 rating
        </div>
      </div>
    </div>
  </header>;
const Testimonials = () => <section className="section-padding pt-[30px] pb-[30px]" data-id="element-22">
    <div className="wrap" data-id="element-23">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-24">
        {[{
        quote: `"Organicé toda mi cursada de Ganancias en un fin de semana. Ahora sé exactamente qué hacer en cada ejercicio en lugar de mirar el enunciado y bloquearme. Aprobé el parcial dos semanas después de empezar."`,
        author: 'Camila S.',
        meta: 'Buenos Aires — Contador Público, UBA',
        avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=70&auto=format&fit=crop'
      }, {
        quote: `"El método me cambió la forma de estudiar. Aprobé IVA en mi primer intento después de estar dos cuatrimestres dándole vueltas al mismo tema. Y sin memorizar de más."`,
        author: 'Federico L.',
        meta: 'Rosario — Contador Público, UNR',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=70&auto=format&fit=crop'
      }, {
        quote: `"Adapto el orden de estudio según la materia que tengo esa semana en 5 minutos. Antes me llevaba horas buscar ejercicios resueltos sueltos en grupos de WhatsApp."`,
        author: 'Agustina P.',
        meta: 'Córdoba — Contador Público, UNC',
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&q=70&auto=format&fit=crop'
      }, {
        quote: `"Venía arrastrando Contabilidad Superior hacía un año. Con el tomo del ajuste por inflación lo entendí en una tarde y la rendí libre. No lo podía creer."`,
        author: 'Martín D.',
        meta: 'La Plata — Contador Público, UNLP',
        avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&q=70&auto=format&fit=crop'
      }, {
        quote: `"Los ejercicios resueltos paso a paso son oro. Dejé de depender de que un compañero me explique y empecé a resolver sola. Aprobé Costos con 8."`,
        author: 'Lucía R.',
        meta: 'Mendoza — Contador Público, UNCuyo',
        avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&q=70&auto=format&fit=crop'
      }, {
        quote: `"Trabajo en un estudio contable y estudio a la vez. El kit me ordenó las dos cosas: aplico lo que leo directo en el laburo y rindo más tranquilo."`,
        author: 'Nicolás V.',
        meta: 'CABA — Contador Público, UADE',
        avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&q=70&auto=format&fit=crop'
      }, {
        quote: `"El simulador de examen oral me sacó el miedo a rendir. Practiqué las 50 preguntas con una amiga y llegué al final segura de lo que iba a decir."`,
        author: 'Sofía M.',
        meta: 'Santa Fe — Contador Público, UNL',
        avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&q=70&auto=format&fit=crop'
      }].map((t, i) => <div key={i} className="bg-card-bg border border-card-border rounded-2xl p-6 hover:border-gold/30 transition-colors flex flex-col" data-id="element-25">
            <div className="flex gap-0.5 mb-3.5" data-id="element-26">
              {[...Array(5)].map((_, j) => <Star key={j} className="w-[15px] h-[15px] text-gold-light fill-gold-light" data-id="element-27" />)}
            </div>
            <p className="text-[14.5px] text-text-primary mb-6 leading-[1.65] flex-1" data-id="element-28">
              {t.quote}
            </p>
            <div className="flex items-center gap-3" data-id="element-29">
              <img src={t.avatar} alt={t.author} loading="lazy" decoding="async" className="w-10 h-10 rounded-full object-cover border border-navy/10" data-id="element-30" />
              <div data-id="element-31">
                <p className="text-[13.5px] font-bold text-navy leading-tight" data-id="element-32">
                  {t.author}
                </p>
                <p className="text-[12px] text-text-tertiary leading-tight mt-0.5" data-id="element-33">
                  {t.meta}
                </p>
              </div>
            </div>
          </div>)}
      </div>
    </div>
  </section>;
const PainPoints = () => {
  const pains = [{
    icon: XCircle,
    title: 'Llegás al parcial sin saber por dónde empezar',
    desc: 'Sin saber qué paso seguir cuando el ejercicio no es igual al del apunte.'
  }, {
    icon: FileText,
    title: 'Ejercicios sin solución',
    desc: 'Los apuntes de la cátedra plantean casos pero nunca los resuelven completos.'
  }, {
    icon: Brain,
    title: 'Te bloqueás en el examen',
    desc: 'Sabés la teoría, pero al ver el enunciado real te quedás en blanco.'
  }, {
    icon: HelpCircle,
    title: 'Siempre la misma duda',
    desc: 'No sabés si el dato del enunciado va en la base o en la alícuota.'
  }, {
    icon: TrendingDown,
    title: 'Ganancias inalcanzable',
    desc: 'Te falta un método claro para encarar la liquidación completa.'
  }, {
    icon: MessageCircle,
    title: 'Apuntes sueltos de WhatsApp',
    desc: 'Sin un sistema claro que respalde tu estudio.'
  }, {
    icon: Clock,
    title: 'No sabés organizar tu tiempo',
    desc: 'Tu cursada se vuelve un caos según la materia del día.'
  }, {
    icon: CalendarX,
    title: 'Fin de cuatrimestre sin avanzar',
    desc: 'Sin un método de resolución, las materias se acumulan.'
  }];
  return <section className="section-padding pt-[30px] pb-[30px]" data-id="element-34">
      <div className="wrap" data-id="element-35">
        <div className="text-center max-w-[640px] mx-auto mb-12" data-id="element-36">
          <h2 className="font-display font-bold text-[clamp(26px,3.5vw,38px)] text-navy mb-3.5 tracking-[-0.01em]" data-id="element-37">
            ¿Te pasa esto?
          </h2>
          <p className="text-[16.5px] text-text-secondary" data-id="element-38">
            Si estudiás Contador Público en Argentina, esto te suena familiar
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-39">
          {pains.map((pain, i) => <div key={i} className="bg-card-bg border border-card-border rounded-xl p-6 flex flex-col items-center text-center hover:-translate-y-1 transition-transform duration-300" data-id="element-40">
              <div className="w-12 h-12 rounded-full bg-red/10 flex items-center justify-center mb-4" data-id="element-41">
                <pain.icon className="w-[22px] h-[22px] text-red" data-id="element-42" />
              </div>
              <h4 className="font-display font-bold text-[15px] text-navy mb-2 leading-snug" data-id="element-43">
                {pain.title}
              </h4>
              <p className="text-[13.5px] text-text-tertiary leading-relaxed" data-id="element-44">
                {pain.desc}
              </p>
            </div>)}
        </div>
        <p className="text-center mt-8 text-lg font-semibold text-gold-light" data-id="element-45">
          ¿Cuántos cuatrimestres más vas a estudiar de memoria sin entender?
        </p>
      </div>
    </section>;
};
const TargetAudience = () => {
  const audience = [{
    icon: GraduationCap,
    title: 'Estudiantes estancados',
    desc: 'De 2.º a 5.º año que repiten el mismo bloqueo en cada parcial sin importar cuánto estudien.'
  }, {
    icon: BookOpen,
    title: 'Estudiás solo o en grupo',
    desc: 'Con tus apuntes y una calculadora te alcanza. Sin academias caras, sin profesores particulares.'
  }, {
    icon: Briefcase,
    title: 'Estudiantes que ya trabajan en estudios contables',
    desc: 'Querés aplicar lo que aprendés directo en tu primer empleo.',
    isNew: true
  }, {
    icon: Target,
    title: 'Buscás aprobar las materias más pesadas',
    desc: 'Vas por Ganancias, IVA, Contabilidad Superior, Costos o Derecho.'
  }];
  return <section className="section-padding pt-[30px] pb-[30px]" data-id="element-46">
      <div className="wrap" data-id="element-47">
        <div className="text-center max-w-[640px] mx-auto mb-12" data-id="element-48">
          <h2 className="font-display font-bold text-[clamp(26px,3.5vw,38px)] text-navy mb-3.5 tracking-[-0.01em]" data-id="element-49">
            ¿Para quién es?
          </h2>
          <p className="text-[16.5px] text-text-secondary" data-id="element-50">
            Diseñado para vos si querés dejar de atrasarte con la carrera
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-51">
          {audience.map((item, i) => <div key={i} className="bg-card-bg border border-card-border rounded-xl p-6 flex gap-4 items-start relative hover:border-violet/30 hover:shadow-[0_8px_24px_rgba(79,70,229,0.08)] transition-all" data-id="element-52">
              {item.isNew && <span className="absolute -top-2.5 right-4 bg-coral text-white text-[10.5px] font-extrabold px-2.5 py-1 rounded-md tracking-[0.05em]" data-id="element-53">
                  NUEVO
                </span>}
              <div className="w-11 h-11 rounded-lg bg-violet-light/10 flex items-center justify-center shrink-0" data-id="element-54">
                <item.icon className="w-[22px] h-[22px] text-violet-light" data-id="element-55" />
              </div>
              <div data-id="element-56">
                <h4 className="font-display font-bold text-[15.5px] text-navy mb-1" data-id="element-57">
                  {item.title}
                </h4>
                <p className="text-[13.5px] text-text-tertiary leading-relaxed" data-id="element-58">
                  {item.desc}
                </p>
              </div>
            </div>)}
        </div>
        <p className="text-center mt-6 text-[14.5px]" data-id="element-59">
          ¿No estás seguro si es para vos?{' '}
          <a href="#garantia" className="text-gold-light font-semibold border-b border-gold/40 hover:text-gold transition-colors" data-id="element-60">
            Tenés 7 días de garantía →
          </a>
        </p>
      </div>
    </section>;
};
const StatsBand = () => <section className="bg-stats-band border-y border-card-border py-12" data-id="element-61">
    <div className="wrap" data-id="element-62">
      <p className="text-center mb-2.5" data-id="element-63">
        <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-violet-light/15 text-violet-light" data-id="element-64">
          SISTEMA N.º 1 EN ARGENTINA
        </span>
      </p>
      <h2 className="text-center font-display font-bold text-[clamp(24px,3vw,32px)] text-navy mb-2 tracking-[-0.01em]" data-id="element-65">
        Mundo Contador al Día
      </h2>

      <div className="flex justify-center my-4" data-id="element-66">
        <img src="https://cdn.magicpatterns.com/uploads/r6DApXJ9HJ1wV1v5Q5TQdx/logo_(2).png" alt="INFODIGITALCORE" loading="lazy" decoding="async" className="w-[280px] sm:w-[360px] max-w-full h-auto object-contain" data-id="element-67" />
      </div>

      <div className="grid grid-cols-2 gap-4 max-w-[480px] mx-auto mb-10" data-id="element-68">
        <div className="text-center" data-id="element-69">
          <div className="font-display text-[34px] font-extrabold text-gold-light leading-none" data-id="element-70">
            +300
          </div>
          <div className="text-[13px] text-text-tertiary mt-1" data-id="element-71">
            páginas de contenido
          </div>
        </div>
        <div className="text-center" data-id="element-72">
          <div className="font-display text-[34px] font-extrabold text-gold-light leading-none" data-id="element-73">
            40+
          </div>
          <div className="text-[13px] text-text-tertiary mt-1" data-id="element-74">
            ejercicios resueltos
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2.5 justify-center mb-8" data-id="element-75">
        {['Método', 'Liquidación', 'Planificación', 'Razonamiento', 'Práctica', 'Errores frecuentes', 'Normativa', 'Casos reales', 'Estrategia de examen'].map(pill => <span key={pill} className="text-[13px] font-semibold text-text-secondary bg-white/5 border border-card-border px-4 py-2 rounded-full" data-id="element-76">
            {pill}
          </span>)}
      </div>

      <p className="text-center text-text-tertiary text-[13.5px] mb-5" data-id="element-77">
        Funciona desde hoy en tu casa, biblioteca o el bar de la facultad
      </p>

      <div className="flex flex-wrap gap-2 justify-center" data-id="element-78">
        {['Impuesto a las Ganancias', 'IVA', 'Contabilidad Superior', 'Costos y Gestión', 'Derecho Comercial y Laboral', 'Método C.A.R.G.A'].map(skill => <span key={skill} className="text-[12.5px] font-semibold text-violet-light bg-violet-light/10 px-3.5 py-1.5 rounded-full" data-id="element-79">
            {skill}
          </span>)}
      </div>
    </div>
  </section>;
const Modules = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const modules = [{
    num: 'T1',
    color: '#4f46e5',
    title: 'Impuesto a las Ganancias: de cero al examen',
    tag: 'La materia que más aplaza, resuelta',
    desc: 'Liquidación completa de persona física y jurídica, con el razonamiento visible en cada paso.',
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=150&q=70&auto=format&fit=crop',
    points: ['Liquidación paso a paso', 'Deducciones admitidas', 'Artículos clave del examen']
  }, {
    num: 'T2',
    color: '#14b8a6',
    title: 'IVA: determinación mensual sin errores',
    tag: 'Débito, crédito y posición de una vez',
    desc: 'El método completo para liquidar IVA mes a mes, con la empresa hilo conductor resuelta de punta a punta.',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66cb85?w=150&q=70&auto=format&fit=crop',
    points: ['Débito y crédito fiscal', 'Casos especiales', 'Simulación de DDJJ']
  }, {
    num: 'T3',
    color: '#f59e0b',
    title: 'Contabilidad Superior sin miedo',
    tag: 'Ajuste por inflación y VPP resueltos',
    desc: 'El ajuste explicado desde la lógica, no desde la memorización, con estados contables completos.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=150&q=70&auto=format&fit=crop',
    points: ['Ajuste por inflación', 'VPP', 'Consolidación']
  }, {
    num: 'T4',
    color: '#22c55e',
    title: 'Costos y Gestión aplicada',
    tag: 'Del número a la decisión real',
    desc: 'Costeo, punto de equilibrio y decisiones gerenciales con una empresa industrial real de ejemplo.',
    image: 'https://images.unsplash.com/photo-1580519542036-ed47f3e42218?w=150&q=70&auto=format&fit=crop',
    points: ['Costeo ABC', 'Punto de equilibrio', 'Presupuesto maestro']
  }, {
    num: 'T5',
    color: '#f97316',
    title: 'El método para rendir cualquier examen',
    tag: 'C.A.R.G.A: tu sistema de resolución',
    desc: 'El método que aplica a todas las materias de la carrera, no solo a una. Funciona en parciales y orales.',
    image: 'https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?w=150&q=70&auto=format&fit=crop',
    points: ['Comprendo', 'Analizo', 'Relaciono', 'Genero borrador', 'Arranco']
  }, {
    num: 'T6',
    color: '#ec4899',
    title: 'Derecho Comercial y Laboral',
    tag: 'Para contadores, en lenguaje contable',
    desc: 'Tipos societarios, liquidación de haberes e indemnizaciones explicados desde el ángulo del contador.',
    image: 'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?w=150&q=70&auto=format&fit=crop',
    points: ['Tipos societarios', 'Liquidación de sueldos', 'Indemnizaciones']
  }];
  return <section id="contenido" className="section-padding pt-[30px] pb-[30px]" data-id="element-80">
      <div className="wrap" data-id="element-81">
        <div className="text-center max-w-[640px] mx-auto mb-12" data-id="element-82">
          <h2 className="font-display font-bold text-[clamp(26px,3.5vw,38px)] text-navy mb-3.5 tracking-[-0.01em]" data-id="element-83">
            Los 6 tomos
          </h2>
          <p className="text-[16.5px] text-text-secondary" data-id="element-84">
            Tu hoja de ruta para aprobar la carrera completa
          </p>
        </div>
        <div className="flex flex-col gap-3" data-id="element-85">
          {modules.map((mod, i) => {
          const isOpen = openIndex === i;
          return <div key={i} className="rounded-2xl border bg-card-bg transition-all cursor-pointer group px-4 py-4 shadow-[0_1px_2px_rgba(26,26,46,0.04)] hover:shadow-[0_8px_24px_rgba(79,70,229,0.08)]" style={{
            borderColor: isOpen ? mod.color : 'rgba(79,70,229,0.14)',
            backgroundColor: isOpen ? `${mod.color}0d` : '#FFFFFF'
          }} onClick={() => setOpenIndex(isOpen ? null : i)} data-id="element-86">
                <div className="flex items-center gap-4" data-id="element-87">
                  <div className="relative w-12 h-12 rounded-xl shrink-0 overflow-hidden transition-transform duration-300" style={{
                transform: isOpen ? 'scale(1.05)' : 'scale(1)'
              }} data-id="element-88">
                    <img src={mod.image} alt={mod.title} loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-89" />
                    <div className="absolute inset-0 flex items-center justify-center font-display font-bold text-sm text-white" style={{
                  backgroundColor: `${mod.color}cc`
                }} data-id="element-90">
                      {mod.num}
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-91">
                    <h4 className="font-display font-bold text-[16px] md:text-[17px] mb-1 transition-colors text-navy" style={{
                  color: isOpen ? mod.color : undefined
                }} data-id="element-92">
                      {mod.title}
                    </h4>
                    <span className="text-[12px] md:text-[13px] text-text-tertiary font-medium block" data-id="element-93">
                      {mod.tag}
                    </span>
                  </div>
                  <ChevronDown className="w-5 h-5 shrink-0 transition-transform duration-300 text-text-tertiary" style={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                color: isOpen ? mod.color : undefined
              }} data-id="element-94" />
                </div>

                <AnimatePresence initial={false} data-id="element-95">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-96">
                      <div className="pt-4 pl-16 pr-4" data-id="element-97">
                        <p className="text-[13.5px] md:text-sm text-text-secondary mb-3.5 leading-relaxed" data-id="element-98">
                          {mod.desc}
                        </p>
                        <div className="flex flex-wrap gap-2" data-id="element-99">
                          {mod.points.map(pt => <span key={pt} className="text-[11.5px] md:text-xs font-semibold px-2.5 py-1 rounded-md border" style={{
                      color: mod.color,
                      backgroundColor: `${mod.color}12`,
                      borderColor: `${mod.color}33`
                    }} data-id="element-100">
                              {pt}
                            </span>)}
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
        <p className="text-center mt-8 text-[13.5px] text-text-secondary bg-violet/5 border border-violet/10 py-2.5 rounded-lg max-w-sm mx-auto" data-id="element-101">
          Cada tomo tiene entre 50 y 60 páginas con ejercicios 100% resueltos
        </p>
      </div>
    </section>;
};
const Bonuses = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const bonuses = [{
    num: 'BONO 01',
    title: 'Planilla de liquidación de Ganancias e IVA',
    desc: 'Excel listo para completar con la misma lógica que usa un estudio contable real.',
    image: 'https://images.unsplash.com/photo-1543286386-2e659306cd6c?w=150&q=70&auto=format&fit=crop'
  }, {
    num: 'BONO 02',
    title: 'Checklist del examen',
    desc: 'Los 40 puntos que revisás antes de entregar para no perder puntos evitables.',
    image: 'https://images.unsplash.com/photo-1598791318878-10e76d178023?w=150&q=70&auto=format&fit=crop'
  }, {
    num: 'BONO 03',
    title: 'Guía de fórmulas y artículos clave',
    desc: 'Referencia rápida imprimible para tener al lado mientras estudiás.',
    image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=150&q=70&auto=format&fit=crop'
  }, {
    num: 'BONO 04',
    title: 'Caso empresa completa',
    desc: 'Un año fiscal entero resuelto, integrando las 6 materias del kit.',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=150&q=70&auto=format&fit=crop'
  }, {
    num: 'BONO 05',
    title: 'Mapa mental de la carrera',
    desc: 'Cómo se conectan todas las materias entre sí, desde 1.º hasta 5.º año.',
    image: 'https://images.unsplash.com/photo-1503694978374-8a2fa686963a?w=150&q=70&auto=format&fit=crop'
  }, {
    num: 'BONO 06',
    title: 'Simulador de examen oral',
    desc: '50 preguntas con respuesta modelo para practicar solo o en grupo de estudio.',
    image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=150&q=70&auto=format&fit=crop'
  }];
  return <section className="section-padding pt-[30px] pb-[30px]" data-id="element-102">
      <div className="wrap" data-id="element-103">
        <div className="text-center max-w-[640px] mx-auto mb-12" data-id="element-104">
          <h2 className="font-display font-bold text-[clamp(26px,3.5vw,38px)] text-navy mb-3.5 tracking-[-0.01em]" data-id="element-105">
            6 bonos estratégicos
          </h2>
          <p className="text-[16.5px] text-text-secondary" data-id="element-106">
            Sin costo extra — incluidos en todas las ofertas
          </p>
        </div>
        <div className="flex flex-col gap-3" data-id="element-107">
          {bonuses.map((bonus, i) => {
          const isOpen = openIndex === i;
          return <div key={i} className={`rounded-2xl border bg-card-bg transition-all cursor-pointer group px-4 py-4 shadow-[0_1px_2px_rgba(26,26,46,0.04)] hover:shadow-[0_8px_24px_rgba(245,158,11,0.1)] ${isOpen ? 'border-gold bg-gold/5' : 'border-card-border'}`} onClick={() => setOpenIndex(isOpen ? null : i)} data-id="element-108">
                <div className="flex items-center gap-4" data-id="element-109">
                  <div className="relative w-12 h-12 rounded-xl shrink-0 overflow-hidden transition-transform duration-300" style={{
                transform: isOpen ? 'scale(1.05)' : 'scale(1)'
              }} data-id="element-110">
                    <img src={bonus.image} alt={bonus.title} loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-111" />
                    <div className="absolute inset-0 flex items-center justify-center font-display font-bold text-sm text-white bg-gold/80" data-id="element-112">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-113">
                    <h4 className={`font-display font-bold text-[16px] md:text-[17px] mb-1 transition-colors ${isOpen ? 'text-gold-light' : 'text-navy group-hover:text-gold-light'}`} data-id="element-114">
                      {bonus.title}
                    </h4>
                    <span className="text-[12px] md:text-[13px] text-gold-light font-semibold tracking-[0.04em] block" data-id="element-115">
                      {bonus.num}
                    </span>
                  </div>
                  <span className="text-[10px] font-extrabold text-white bg-green px-2 py-1 rounded-md shrink-0" data-id="element-116">
                    GRATIS
                  </span>
                  <ChevronDown className={`w-5 h-5 text-text-tertiary shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-gold-light' : ''}`} data-id="element-117" />
                </div>

                <AnimatePresence initial={false} data-id="element-118">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-119">
                      <div className="pt-4 pl-16 pr-4" data-id="element-120">
                        <p className="text-[13.5px] md:text-sm text-text-secondary leading-relaxed" data-id="element-121">
                          {bonus.desc}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
        <div className="text-center mt-8 p-5 rounded-xl bg-gold/10 border border-gold/25 max-w-3xl mx-auto" data-id="element-122">
          <span className="text-text-tertiary line-through text-[15px]" data-id="element-123">
            Valor total: $47.000
          </span>
          <span className="font-display font-extrabold text-2xl text-gold-light ml-2.5" data-id="element-124">
            GRATIS
          </span>
        </div>
      </div>
    </section>;
};
const Comparison = () => {
  const rows = [{
    bad: '3-5 hs/semana buscando ejercicios resueltos',
    good: '10 min/semana siguiendo el tomo de tu materia'
  }, {
    bad: 'Siempre el mismo bloqueo en el parcial',
    good: '40+ ejercicios resueltos con razonamiento visible'
  }, {
    bad: 'Aplazos por no saber por dónde empezar',
    good: 'Método C.A.R.G.A para encarar cualquier ejercicio'
  }, {
    bad: 'Sin estructura para liquidar Ganancias o IVA',
    good: '6 tomos con liquidación completa paso a paso'
  }, {
    bad: 'Cursada estancada y frustrante',
    good: 'Avance medible parcial a parcial'
  }, {
    bad: 'Ejercicios sueltos sin solución',
    good: 'Casos resueltos completos, con errores marcados'
  }, {
    bad: 'Sin saber organizar el estudio',
    good: 'Checklist, planilla y mapa mental de la carrera'
  }, {
    bad: 'Cero estructura visible de avance',
    good: 'Sistema directo a la próxima materia aprobada'
  }];
  return <section className="section-padding" data-id="element-125">
      <div className="wrap-narrow" data-id="element-126">
        <div className="text-center max-w-[640px] mx-auto mb-12" data-id="element-127">
          <h2 className="font-display font-bold text-[clamp(26px,3.5vw,38px)] text-navy tracking-[-0.01em]" data-id="element-128">
            ¿Cuánto te cuesta no tenerlo?
          </h2>
        </div>
        {/* Desktop / tablet: two-column table */}
        <div className="hidden md:block border border-card-border rounded-2xl overflow-hidden" data-id="element-129">
          <div className="grid grid-cols-2 text-[13px] font-bold tracking-[0.04em] uppercase" data-id="element-130">
            <div className="text-red bg-red/10 p-4 px-6 flex items-center gap-2" data-id="element-131">
              <XCircle className="w-4 h-4 shrink-0" data-id="element-132" />
              Sin el sistema
            </div>
            <div className="text-green bg-green/10 p-4 px-6 flex items-center gap-2" data-id="element-133">
              <Check className="w-4 h-4 shrink-0" data-id="element-134" />
              Con el sistema
            </div>
          </div>
          {rows.map((row, i) => <div key={i} className="grid grid-cols-2 border-t border-card-border" data-id="element-135">
              <div className="p-4.5 px-6 text-[14.5px] flex items-start gap-2.5 text-text-secondary bg-red/5" data-id="element-136">
                <XCircle className="w-4 h-4 text-red shrink-0 mt-0.5" data-id="element-137" />
                <span data-id="element-138">{row.bad}</span>
              </div>
              <div className="p-4.5 px-6 text-[14.5px] flex items-start gap-2.5 text-text-primary bg-green/5 font-medium" data-id="element-139">
                <Check className="w-4 h-4 text-green shrink-0 mt-0.5" data-id="element-140" />
                <span data-id="element-141">{row.good}</span>
              </div>
            </div>)}
        </div>

        {/* Mobile: stacked before/after cards, centered */}
        <div className="md:hidden flex flex-col gap-4" data-id="element-142">
          {rows.map((row, i) => <div key={i} className="rounded-2xl border border-card-border bg-card-bg overflow-hidden" data-id="element-143">
              <div className="p-4 flex items-start gap-3 bg-red/5" data-id="element-144">
                <span className="w-7 h-7 rounded-full bg-red/15 flex items-center justify-center shrink-0" data-id="element-145">
                  <X className="w-4 h-4 text-red" strokeWidth={3} data-id="element-146" />
                </span>
                <div data-id="element-147">
                  <span className="block text-[10.5px] font-bold tracking-[0.06em] uppercase text-red mb-0.5" data-id="element-148">
                    Sin el sistema
                  </span>
                  <span className="text-[14px] text-text-secondary leading-snug" data-id="element-149">
                    {row.bad}
                  </span>
                </div>
              </div>
              <div className="p-4 flex items-start gap-3 bg-green/5 border-t border-card-border" data-id="element-150">
                <span className="w-7 h-7 rounded-full bg-green/15 flex items-center justify-center shrink-0" data-id="element-151">
                  <Check className="w-4 h-4 text-green" strokeWidth={3} data-id="element-152" />
                </span>
                <div data-id="element-153">
                  <span className="block text-[10.5px] font-bold tracking-[0.06em] uppercase text-green mb-0.5" data-id="element-154">
                    Con el sistema
                  </span>
                  <span className="text-[14px] text-text-primary font-medium leading-snug" data-id="element-155">
                    {row.good}
                  </span>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};
const UrgencyBar = () => {
  const [timeLeft, setTimeLeft] = useState({
    h: 23,
    m: 46,
    s: 35
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let {
          h,
          m,
          s
        } = prev;
        if (h === 0 && m === 0 && s === 0) return prev;
        s--;
        if (s < 0) {
          s = 59;
          m--;
        }
        if (m < 0) {
          m = 59;
          h--;
        }
        return {
          h,
          m,
          s
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const pad = (n: number) => String(n).padStart(2, '0');
  return <section className="bg-urgency-bar border-y border-red/20 py-7" data-id="element-156">
      <div className="wrap" data-id="element-157">
        <div className="flex flex-wrap items-center justify-center md:justify-between gap-5 text-center md:text-left" data-id="element-158">
          <div data-id="element-159">
            <p className="text-[13px] font-bold tracking-[0.05em] uppercase text-red mb-0.5" data-id="element-160">
              Oferta en vivo · +$8.000 después
            </p>
            <span className="text-[13px] text-navy/70" data-id="element-161">
              El lanzamiento termina pronto y no vuelve a bajar
            </span>
          </div>
          <div className="flex gap-2.5" data-id="element-162">
            {[{
            v: pad(timeLeft.h),
            l: 'Horas'
          }, {
            v: pad(timeLeft.m),
            l: 'Min'
          }, {
            v: pad(timeLeft.s),
            l: 'Seg'
          }].map((cd, i) => <div key={i} className="bg-white rounded-lg py-2 px-2.5 text-center min-w-[54px] md:min-w-[64px] shadow-[0_2px_8px_rgba(220,38,38,0.12)]" data-id="element-163">
                <div className="font-display text-[20px] md:text-[26px] font-extrabold text-red leading-none" data-id="element-164">
                  {cd.v}
                </div>
                <div className="text-[10px] text-navy/60 uppercase tracking-[0.05em] mt-0.5" data-id="element-165">
                  {cd.l}
                </div>
              </div>)}
          </div>
        </div>
        <div className="mt-4.5" data-id="element-166">
          <div className="flex justify-between text-[12.5px] text-navy/80 mb-1.5 font-semibold" data-id="element-167">
            <span data-id="element-168">Cupos restantes</span>
            <span data-id="element-169">68% tomados</span>
          </div>
          <div className="h-2 rounded-full bg-white/70 overflow-hidden" data-id="element-170">
            <div className="h-full w-[68%] bg-red rounded-full" data-id="element-171"></div>
          </div>
        </div>
      </div>
    </section>;
};
const OFFERS = [{
  id: 'completo',
  tier: 'Pack Completo',
  includes: '6 tomos + 6 bonos + 2 tomos exclusivos',
  oldPrice: '$43.990',
  price: '$29.990',
  save: 'Ahorrás $14.000',
  features: ['Todo lo del Kit Aprobado', '+ Tomo 7: Finanzas (VAN, TIR, valuación)', '+ Tomo 8: Cómo recibirte (trabajo final y defensa)'],
  featured: true,
  url: 'https://checkout.infodigitalcore.shop/cart/49814052536559:1'
}, {
  id: 'aprobado',
  tier: 'Kit Aprobado',
  includes: '6 tomos + 6 bonos estratégicos',
  oldPrice: '$34.990',
  price: '$19.990',
  save: 'Ahorrás $15.000',
  features: ['6 tomos con ejercicios resueltos paso a paso', 'Método C.A.R.G.A para rendir cualquier examen', '6 bonos: planilla, checklist, fórmulas y más'],
  featured: false,
  url: 'https://checkout.infodigitalcore.shop/cart/49814052143343:1'
}] as const;
type OfferId = (typeof OFFERS)[number]['id'];
const OfferSelector = ({
  idPrefix
}: {
  idPrefix: string;
}) => {
  const [selectedId, setSelectedId] = useState<OfferId>('completo');
  const selected = OFFERS.find(o => o.id === selectedId) ?? OFFERS[0];
  return <div className="max-w-[760px] mx-auto" data-id="element-172">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 items-stretch" role="radiogroup" aria-label="Elegí tu Kit Contador al Día" data-id="element-173">
        {OFFERS.map(offer => {
        const isSelected = offer.id === selectedId;
        return <button key={offer.id} type="button" role="radio" aria-checked={isSelected} onClick={() => setSelectedId(offer.id)} className={`relative text-left rounded-2xl p-4 md:p-5 flex flex-col transition-all duration-200 ${isSelected ? 'border-2 border-gold bg-gradient-to-br from-gold/10 to-card-bg shadow-[0_0_30px_rgba(245,158,11,0.15)]' : 'border border-card-border bg-card-bg hover:border-violet/40 hover:shadow-[0_8px_24px_rgba(79,70,229,0.08)]'} ${offer.featured ? 'mt-3 sm:mt-0' : ''}`} data-id="element-174">
              {offer.featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-[#1a1205] text-[10.5px] md:text-[11px] font-extrabold px-3 py-1 rounded-full tracking-[0.03em] whitespace-nowrap" data-id="element-175">
                  ⭐ Más elegido
                </span>}

              <div className="flex items-start justify-between gap-2 mb-2" data-id="element-176">
                <div data-id="element-177">
                  <div className={`text-[11.5px] font-bold tracking-[0.06em] uppercase ${isSelected ? 'text-gold-light' : 'text-text-tertiary'}`} data-id="element-178">
                    {offer.tier}
                  </div>
                  <div className="text-[12.5px] text-text-secondary leading-snug" data-id="element-179">
                    {offer.includes}
                  </div>
                </div>
                <span className={`mt-0.5 w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${isSelected ? 'border-gold bg-gold' : 'border-text-tertiary bg-transparent'}`} aria-hidden="true" data-id="element-180">
                  {isSelected && <Check className="w-3 h-3 text-[#1a1205]" strokeWidth={3} data-id="element-181" />}
                </span>
              </div>

              <div className="flex items-baseline gap-2 mb-1.5" data-id="element-182">
                <span className="text-sm text-text-tertiary line-through" data-id="element-183">
                  {offer.oldPrice}
                </span>
                <span className="font-display text-[28px] md:text-[32px] font-extrabold text-navy leading-none" data-id="element-184">
                  {offer.price}
                </span>
              </div>
              <span className="inline-block text-[11.5px] font-bold text-green bg-green/10 px-2.5 py-0.5 rounded-md w-fit mb-3" data-id="element-185">
                {offer.save}
              </span>

              <ul className="flex flex-col gap-1.5" data-id="element-186">
                {offer.features.map((feat, i) => <li key={i} className="flex gap-2 text-[12.5px] text-text-secondary items-start leading-snug" data-id="element-187">
                    {feat.startsWith('+') ? <Star className="w-3.5 h-3.5 text-gold-light shrink-0 mt-0.5" data-id="element-188" /> : <Check className="w-3.5 h-3.5 text-green shrink-0 mt-0.5" data-id="element-189" />}
                    {feat}
                  </li>)}
              </ul>
            </button>;
      })}
      </div>

      <a href={selected.url} className="mt-5 w-full font-bold text-base md:text-[17px] px-6 py-4 rounded-xl bg-btn-primary text-[#1a1205] shadow-btn-primary hover:-translate-y-0.5 hover:shadow-btn-primary-hover active:translate-y-0 active:scale-98 transition-all flex items-center justify-center gap-2" data-id="element-190">
        Comprar ahora · {selected.price}
      </a>

      <div className="flex justify-center gap-4 md:gap-4.5 mt-4 flex-wrap" data-id="element-191">
        <span className="text-[11.5px] text-text-tertiary flex items-center gap-1.5" data-id="element-192">
          <CreditCard className="w-3.5 h-3.5 text-gold-light" data-id="element-193" /> Pago seguro
        </span>
        <span className="text-[11.5px] text-text-tertiary flex items-center gap-1.5" data-id="element-194">
          <Clock className="w-3.5 h-3.5 text-gold-light" data-id="element-195" /> Acceso en 5 min
        </span>
        <span className="text-[11.5px] text-text-tertiary flex items-center gap-1.5" data-id="element-196">
          <ShieldCheck className="w-3.5 h-3.5 text-gold-light" data-id="element-197" /> Garantía 7
          días
        </span>
      </div>
    </div>;
};
const Pricing = () => <section id="oferta" className="section-padding" data-id="element-198">
    <div className="wrap" data-id="element-199">
      <div className="text-center mb-2" data-id="element-200">
        <h2 className="font-display font-bold text-[clamp(26px,3.5vw,36px)] text-navy tracking-[-0.01em]" data-id="element-201">
          Elegí tu Kit Contador al Día
        </h2>
      </div>
      <p className="text-center text-text-secondary text-[15px] mb-9" data-id="element-202">
        Ambas opciones incluyen los 6 bonos estratégicos completos
      </p>

      <OfferSelector idPrefix="main" data-id="element-203" />
    </div>
  </section>;
const UpsellDetail = () => <section className="section-padding pt-0" data-id="element-204">
    <div className="wrap" data-id="element-205">
      <div className="bg-card-bg border border-card-border rounded-[18px] p-9" data-id="element-206">
        <div className="text-[12.5px] font-bold text-text-tertiary tracking-[0.05em] mb-1.5" data-id="element-207">
          01 / 02
        </div>
        <div className="font-display text-2xl font-bold text-navy mb-1.5" data-id="element-208">
          ¿Qué agrega el Kit Recibido?
        </div>
        <p className="text-text-secondary text-[14.5px] mb-7" data-id="element-209">
          Para estudiantes que quieren ir un paso más allá (Finanzas o Trabajo
          Final)
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-7" data-id="element-210">
          <div className="rounded-xl p-5 bg-red/5 border border-red/20" data-id="element-211">
            <h5 className="text-[13px] font-bold uppercase tracking-[0.04em] mb-2.5 text-red" data-id="element-212">
              Sin Kit Recibido
            </h5>
            <p className="text-sm text-text-secondary leading-[1.6]" data-id="element-213">
              Llegás a Finanzas o al Trabajo Final sin saber por dónde empezar,
              y la fecha de defensa se acerca sin un plan claro.
            </p>
          </div>
          <div className="rounded-xl p-5 bg-green/5 border border-green/20" data-id="element-214">
            <h5 className="text-[13px] font-bold uppercase tracking-[0.04em] mb-2.5 text-green" data-id="element-215">
              Con Kit Recibido
            </h5>
            <p className="text-sm text-text-secondary leading-[1.6]" data-id="element-216">
              Sistema completo de VAN, TIR y valuación de empresas + guía paso a
              paso para armar y defender tu Trabajo Final sin bloquearte.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-217">
          <div className="flex gap-3.5 p-4.5 bg-violet/5 border border-violet/10 rounded-xl" data-id="element-218">
            <div className="w-9 h-9 rounded-lg bg-gold text-[#1a1205] flex items-center justify-center font-extrabold text-sm shrink-0" data-id="element-219">
              T7
            </div>
            <div data-id="element-220">
              <h6 className="text-[14.5px] font-bold text-navy mb-1" data-id="element-221">
                Finanzas para el examen final
              </h6>
              <p className="text-[13px] text-text-tertiary leading-relaxed" data-id="element-222">
                VAN, TIR, WACC y valuación de empresas, explicados desde la
                intuición y resueltos con casos reales.
              </p>
            </div>
          </div>
          <div className="flex gap-3.5 p-4.5 bg-violet/5 border border-violet/10 rounded-xl" data-id="element-223">
            <div className="w-9 h-9 rounded-lg bg-gold text-[#1a1205] flex items-center justify-center font-extrabold text-sm shrink-0" data-id="element-224">
              T8
            </div>
            <div data-id="element-225">
              <h6 className="text-[14.5px] font-bold text-navy mb-1" data-id="element-226">
                Cómo recibirte
              </h6>
              <p className="text-[13px] text-text-tertiary leading-relaxed" data-id="element-227">
                Cómo elegir tema, estructurar tu trabajo final, armar la
                presentación y defenderlo ante el tribunal.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>;
const Guarantee = () => <section id="garantia" className="section-padding" data-id="element-228">
    <div className="wrap text-center max-w-[680px] mx-auto" data-id="element-229">
      <div className="w-[88px] h-[88px] rounded-full bg-guarantee-badge flex items-center justify-center mx-auto mb-6 shadow-guarantee" data-id="element-230">
        <ShieldCheck className="w-10 h-10 text-[#1a1205]" strokeWidth={2.2} data-id="element-231" />
      </div>
      <h3 className="font-display text-[26px] font-bold text-navy mb-3.5 tracking-[-0.01em]" data-id="element-232">
        7 días de garantía total
      </h3>
      <p className="text-[15.5px] text-text-secondary mb-2" data-id="element-233">
        7 días para probarlo o te devolvemos el 100%
      </p>
      <p className="text-[13px] text-text-tertiary" data-id="element-234">
        Abrís los tomos, probás la planilla y el checklist en tu próximo
        parcial. Si no es lo que esperabas: sin preguntas, sin formularios, sin
        drama. (Tasa de devoluciones &lt; 1%)
      </p>
    </div>
  </section>;
const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = [{
    q: '¿Cómo recibo el kit después de comprar?',
    a: 'Apenas se acredita el pago, te llega un mail con el link de descarga de los PDF y la planilla Excel. El acceso es inmediato, sin esperas ni envíos físicos.'
  }, {
    q: '¿Sirve si recién empiezo la carrera o es solo para los últimos años?',
    a: 'Sirve para toda la carrera. El Tomo 5 (método C.A.R.G.A) y el mapa mental de la carrera están pensados especialmente para 1.º y 2.º año, mientras que Ganancias, IVA, Costos, Contabilidad Superior y Derecho acompañan el resto del recorrido.'
  }, {
    q: '¿Necesito comprar algún programa o me alcanza con el PDF y Excel?',
    a: 'Te alcanza con lo que ya tenés: cualquier lector de PDF y Excel (o Google Sheets, que es gratis). No hace falta comprar ningún software adicional.'
  }, {
    q: '¿Los 6 bonos están incluidos en las dos ofertas?',
    a: 'Sí. Los 6 bonos (planilla, checklist, guía de fórmulas, caso integrador, mapa mental y simulador de oral) están incluidos tanto en el Kit Aprobado como en el Kit Recibido. La única diferencia entre ambos son los 2 tomos exclusivos de Finanzas y Cómo Recibirte.'
  }, {
    q: '¿Sirve para cualquier facultad o solo para algunas universidades?',
    a: 'El kit está armado sobre la lógica y normativa general de la carrera de Contador Público en Argentina, que es común a todas las facultades. Los nombres exactos de las materias pueden variar según tu universidad, pero el contenido y el método aplican igual.'
  }, {
    q: '¿Sirve si mi cátedra tiene un programa distinto al del kit?',
    a: 'Sí. El kit no reemplaza el programa de tu cátedra, lo complementa: te da el método de resolución y los ejercicios que tu apunte no resuelve. El Tomo 3 incluye además una sección para adaptar el contenido a la cátedra específica.'
  }, {
    q: '¿Qué diferencia tiene con apuntes gratis de grupos de WhatsApp?',
    a: 'Los apuntes sueltos casi nunca tienen el razonamiento completo ni una estructura clara. Este kit muestra el paso a paso completo de cada ejercicio, con los errores frecuentes marcados, organizado en un sistema único en vez de archivos dispersos.'
  }, {
    q: '¿Por qué elegir el Kit Recibido y no el Aprobado?',
    a: 'Si estás en el tramo final de la carrera y te quedan Finanzas y el Trabajo Final, el Kit Recibido te da exactamente eso: VAN, TIR y valuación resueltos, más la guía completa para armar y defender tu trabajo final. Si todavía te quedan varios años, el Kit Aprobado te alcanza.'
  }, {
    q: '¿Tiene garantía real?',
    a: 'Sí, 7 días de garantía total. Si en ese plazo sentís que no es lo que esperabas, te devolvemos el 100% sin pedirte explicaciones ni formularios.'
  }];
  return <section className="section-padding" data-id="element-235">
      <div className="wrap-narrow" data-id="element-236">
        <div className="text-center max-w-[640px] mx-auto mb-12" data-id="element-237">
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.08em] uppercase text-gold-light bg-gold/10 px-3.5 py-1.5 rounded-full border border-gold/25 mb-3" data-id="element-238">
            Dudas comunes
          </span>
          <h2 className="font-display font-bold text-[clamp(26px,3.5vw,38px)] text-navy tracking-[-0.01em]" data-id="element-239">
            Preguntas frecuentes
          </h2>
        </div>
        <div data-id="element-240">
          {faqs.map((faq, i) => <div key={i} className="border-b border-card-border" data-id="element-241">
              <button className="w-full flex justify-between items-center py-5 px-1 text-left text-base font-semibold text-navy hover:text-gold-light transition-colors" onClick={() => setOpenIndex(openIndex === i ? null : i)} data-id="element-242">
                {faq.q}
                <ChevronDown className={`w-5 h-5 text-text-tertiary shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180' : ''}`} data-id="element-243" />
              </button>
              <div className={`overflow-hidden transition-all duration-250 ease-in-out ${openIndex === i ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-244">
                <div className="px-1 pb-5 text-[14.5px] text-text-secondary leading-[1.7]" data-id="element-245">
                  {faq.a}
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};
const FinalCTA = () => {
  const [timeLeft, setTimeLeft] = useState({
    h: 23,
    m: 46,
    s: 35
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let {
          h,
          m,
          s
        } = prev;
        if (h === 0 && m === 0 && s === 0) return prev;
        s--;
        if (s < 0) {
          s = 59;
          m--;
        }
        if (m < 0) {
          m = 59;
          h--;
        }
        return {
          h,
          m,
          s
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const pad = (n: number) => String(n).padStart(2, '0');
  return <section className="section-padding text-center" data-id="element-246">
      <div className="wrap" data-id="element-247">
        <h2 className="font-display font-bold text-[clamp(26px,4vw,38px)] text-navy mb-8 tracking-[-0.01em]" data-id="element-248">
          No dejes pasar esta oportunidad
        </h2>
        <p className="text-text-secondary mb-6" data-id="element-249">
          Termina en{' '}
          <strong className="text-navy" data-id="element-250">
            {pad(timeLeft.h)}:{pad(timeLeft.m)}:{pad(timeLeft.s)}
          </strong>
        </p>

        <OfferSelector idPrefix="final" data-id="element-251" />
      </div>
    </section>;
};
const Footer = () => <footer className="py-12 pb-9 border-t border-card-border text-center" data-id="element-252">
    <div className="wrap" data-id="element-253">
      <div className="font-display font-bold text-lg text-navy mb-2.5" data-id="element-254">
        📘 Kit Contador al Día™
      </div>
      <p className="text-[13.5px] text-text-tertiary mb-1.5" data-id="element-255">
        El sistema de estudio N.º 1 para estudiantes de Contador Público en
        Argentina.
      </p>
      <p className="text-[13.5px] text-text-tertiary mb-1.5" data-id="element-256">
        Porque aprobar la carrera no debería costarte cuatrimestres enteros de
        frustración y aplazos.
      </p>

      <div className="flex gap-4.5 justify-center my-4 text-[13px] text-text-secondary" data-id="element-257">
        <a href="mailto:contacto@infodigitalcore.com" className="hover:text-white transition-colors" data-id="element-258">
          soporteIDC1@outlook.com
        </a>
        <span className="text-[12px]" data-id="element-259">·</span>
        <a href="https://instagram.com/infodigitalcore" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-id="element-260">
          @infordigitalcore
        </a>
        <span className="text-[12px]" data-id="element-261">·</span>
      </div>

      <p className="text-text-tertiary mt-4.5 text-[12px]" data-id="element-262">
        © infodigitalcore.com™ 2026 — Todos los derechos reservados.
      </p>
    </div>
  </footer>;
const PURCHASE_NAMES = ['Camila G.', 'Martín R.', 'Sofía L.', 'Lucas P.', 'Valentina M.', 'Nicolás D.', 'Julieta F.', 'Tomás S.', 'Florencia A.', 'Mateo B.', 'Agustina V.', 'Joaquín C.', 'Brenda T.', 'Franco H.', 'Micaela N.', 'Ezequiel O.'];
const PURCHASE_CITIES = ['Buenos Aires', 'Córdoba', 'Rosario, Santa Fe', 'La Plata, Bs. As.', 'Mendoza', 'Mar del Plata, Bs. As.', 'San Miguel de Tucumán', 'Salta', 'Neuquén', 'Bahía Blanca, Bs. As.', 'Santa Fe', 'Resistencia, Chaco', 'Posadas, Misiones', 'San Juan', 'Paraná, Entre Ríos', 'Quilmes, Bs. As.'];
const PURCHASE_OFFERS = ['Pack Completo', 'Kit Aprobado'];
const pickRandom = <T,>(arr: readonly T[]): T => arr[Math.floor(Math.random() * arr.length)];
type Purchase = {
  key: number;
  name: string;
  city: string;
  offer: string;
  minsAgo: number;
};
const PurchaseNotification = () => {
  const [purchase, setPurchase] = useState<Purchase | null>(null);
  useEffect(() => {
    let showTimeout: ReturnType<typeof setTimeout>;
    let hideTimeout: ReturnType<typeof setTimeout>;
    let counter = 0;
    const scheduleNext = (firstRun = false) => {
      // 40–60 seconds between pop-ups (shorter delay for the very first one)
      const delay = firstRun ? 6000 : 40000 + Math.random() * 20000;
      showTimeout = setTimeout(() => {
        counter += 1;
        setPurchase({
          key: counter,
          name: pickRandom(PURCHASE_NAMES),
          city: pickRandom(PURCHASE_CITIES),
          offer: pickRandom(PURCHASE_OFFERS),
          minsAgo: 1 + Math.floor(Math.random() * 8)
        });
        // Keep the toast visible for ~6 seconds, then hide and schedule the next
        hideTimeout = setTimeout(() => {
          setPurchase(null);
          scheduleNext();
        }, 6000);
      }, delay);
    };
    scheduleNext(true);
    return () => {
      clearTimeout(showTimeout);
      clearTimeout(hideTimeout);
    };
  }, []);
  return <div className="fixed bottom-4 left-4 z-[60] pointer-events-none max-w-[calc(100vw-2rem)]" data-id="element-263">
      <AnimatePresence data-id="element-264">
        {purchase && <motion.div key={purchase.key} initial={{
        opacity: 0,
        y: 24,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 16,
        scale: 0.96
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} role="status" aria-live="polite" className="pointer-events-auto flex items-start gap-3 w-[320px] max-w-full bg-card-bg/95 backdrop-blur-md border border-card-border rounded-2xl p-3.5 shadow-[0_12px_40px_rgba(0,0,0,0.5)]" data-id="element-265">
            <div className="w-11 h-11 rounded-xl bg-green/15 flex items-center justify-center shrink-0" data-id="element-266">
              <BadgeCheck className="w-6 h-6 text-green" strokeWidth={2.2} data-id="element-267" />
            </div>
            <div className="min-w-0 flex-1" data-id="element-268">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-269">
                <span className="text-[13.5px] font-bold text-navy truncate" data-id="element-270">
                  {purchase.name}
                </span>
                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-green bg-green/10 px-1.5 py-0.5 rounded-md shrink-0" data-id="element-271">
                  <Check className="w-2.5 h-2.5" strokeWidth={3} data-id="element-272" /> Verificada
                </span>
              </div>
              <p className="text-[12.5px] text-text-secondary leading-snug" data-id="element-273">
                Compró el{' '}
                <span className="text-gold-light font-semibold" data-id="element-274">
                  {purchase.offer}
                </span>
              </p>
              <div className="flex items-center gap-2 mt-1 text-[11px] text-text-tertiary" data-id="element-275">
                <span className="inline-flex items-center gap-0.5" data-id="element-276">
                  <MapPin className="w-3 h-3" data-id="element-277" /> {purchase.city}
                </span>
                <span data-id="element-278">·</span>
                <span data-id="element-279">hace {purchase.minsAgo} min</span>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
const META_PIXEL_ID = '3402382169937662';
export function App() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const w = window as any;
    if (!w.fbq) {
      const n: any = w.fbq = function (...args: any[]) {
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      };
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
    }
    w.fbq('init', META_PIXEL_ID);
    w.fbq('track', 'PageView');
  }, []);
  return <div className="min-h-screen bg-bg-darker font-body text-text-primary selection:bg-violet-light/30" data-id="element-280">
      <Navbar data-id="element-281" />
      <main data-id="element-282">
        <Hero data-id="element-283" />
        <Testimonials data-id="element-284" />
        <PainPoints data-id="element-285" />
        <TargetAudience data-id="element-286" />
        <StatsBand data-id="element-287" />
        <Modules data-id="element-288" />
        <Bonuses data-id="element-289" />
        <Comparison data-id="element-290" />
        <UrgencyBar data-id="element-291" />
        <Pricing data-id="element-292" />
        <UpsellDetail data-id="element-293" />
        <Guarantee data-id="element-294" />
        <FAQ data-id="element-295" />
        <FinalCTA data-id="element-296" />
      </main>
      <Footer data-id="element-297" />
      <PurchaseNotification data-id="element-298" />
    </div>;
}